<template>
  <section>
    <b-button
      v-if="hasRegistraturaAccess()"
      icon-left="bookmark-plus-outline"
      size="is-small"
      type="is-light"
      @click="open"
    >
      Rezerva numere
    </b-button>

    <!-- In timpul rezervarii si in confirmare fereastra se inchide doar din butoane:
         un Escape din reflex nu are voie sa ascunda numerele alocate. -->
    <b-modal
      :active.sync="isCardModalActive"
      has-modal-card
      :can-cancel="reserving || isConfirmation ? [] : ['escape', 'outside']"
    >
      <div class="modal-card reserve-modal">
        <header class="reserve-modal__header">
          <div class="reserve-modal__title">
            <b-icon
              :icon="
                isConfirmation ? 'bookmark-check' : 'bookmark-plus-outline'
              "
              size="is-small"
            />
            <span>{{
              isConfirmation ? confirmationTitle : "Rezervare numere"
            }}</span>
          </div>
          <button
            v-if="!isConfirmation"
            class="delete"
            type="button"
            :disabled="reserving"
            @click="cancel"
          />
        </header>

        <section v-if="isConfirmation" class="reserve-modal__body">
          <p class="reserve-modal__label">Notati numerele alocate:</p>
          <div class="confirmation__numbers">
            <span v-for="item in reservedNow" :key="item.id" class="number-tag">
              {{ item.number }}
            </span>
          </div>
          <p class="reserve-modal__hint">
            Numerele raman vizibile in lista Numere deja rezervate, impreuna cu
            numele celui care le-a rezervat.
          </p>
        </section>

        <section v-else class="reserve-modal__body">
          <p class="reserve-modal__label">Cate numere doriti sa rezervati?</p>
          <div class="quantity-picker">
            <button
              class="quantity-btn"
              type="button"
              :disabled="reserving || numberOfReservedNumbers <= 1"
              @click="adjust(-1)"
            >
              <b-icon icon="minus" size="is-small" />
            </button>
            <span class="quantity-value">{{ numberOfReservedNumbers }}</span>
            <button
              class="quantity-btn"
              type="button"
              :disabled="
                reserving || numberOfReservedNumbers >= maxReservedNumbers
              "
              @click="adjust(1)"
            >
              <b-icon icon="plus" size="is-small" />
            </button>
          </div>
          <p class="reserve-modal__hint">
            Numerele se aloca in momentul confirmarii si vor fi afisate imediat
            dupa.
          </p>

          <!-- Already reserved (collapsible) -->
          <div class="reserved-section">
            <button
              class="reserved-toggle"
              type="button"
              @click="toggleReserved"
            >
              <b-icon
                :icon="showReserved ? 'chevron-up' : 'chevron-down'"
                size="is-small"
              />
              <span>Numere deja rezervate</span>
              <span
                v-if="reservedRegistrationNumbers.length > 0"
                class="reserved-count"
              >
                {{ reservedRegistrationNumbers.length }}
              </span>
            </button>
            <transition name="fade">
              <div v-if="showReserved" class="reserved-table">
                <b-table
                  :loading="isLoading"
                  :paginated="paginated"
                  :per-page="perPage"
                  :striped="true"
                  :hoverable="true"
                  :data="reservedRegistrationNumbers"
                  default-sort="registrationDate"
                  default-sort-direction="desc"
                >
                  <b-table-column
                    label="Numar"
                    field="number"
                    sortable
                    v-slot="props"
                  >
                    <span class="has-text-weight-semibold">{{
                      props.row.number
                    }}</span>
                  </b-table-column>
                  <b-table-column
                    label="Data"
                    field="registrationDate"
                    sortable
                    centered
                    v-slot="props"
                  >
                    {{ props.row.registrationDate | formatDate }}
                  </b-table-column>
                  <b-table-column
                    label="An"
                    field="year"
                    sortable
                    centered
                    v-slot="props"
                  >
                    {{ props.row.year }}
                  </b-table-column>
                  <b-table-column
                    label="Rezervat de"
                    field="username"
                    sortable
                    v-slot="props"
                  >
                    {{ props.row.username || "-" }}
                  </b-table-column>
                  <section class="section" slot="empty">
                    <div class="content has-text-grey has-text-centered">
                      <template v-if="isLoading">
                        <p><b-icon icon="dots-horizontal" size="is-large" /></p>
                        <p>Se incarca datele...</p>
                      </template>
                      <template v-else>
                        <p><b-icon icon="emoticon-sad" size="is-large" /></p>
                        <p>Nu exista inregistrari</p>
                      </template>
                    </div>
                  </section>
                </b-table>
              </div>
            </transition>
          </div>
        </section>

        <footer class="reserve-modal__footer">
          <b-button
            v-if="isConfirmation"
            type="is-primary"
            icon-left="check"
            @click="acknowledge"
          >
            Am notat
          </b-button>
          <template v-else>
            <button
              class="button"
              type="button"
              :disabled="reserving"
              @click="cancel"
            >
              Anulare
            </button>
            <b-button
              type="is-primary"
              icon-left="bookmark-check"
              :loading="reserving"
              :disabled="reserving"
              @click="reserve"
            >
              Rezerva {{ numberOfReservedNumbers }}
              {{ numberOfReservedNumbers === 1 ? "numar" : "numere" }}
            </b-button>
          </template>
        </footer>
      </div>
    </b-modal>
  </section>
</template>

<script>
import { pathOr } from "ramda";
import RegistriesService from "@/services/registry.service";
import { notify } from "@/helpers/notify";
import { accessMixin } from "../../mixins/accessMixin";

// Aceeasi limita ca pe server (RegistryDomainService.MaxReservedNumbersPerRequest).
const MAX_RESERVED_NUMBERS = 100;

export default {
  name: "ReservedNumbers",
  props: {
    registryId: {
      type: Number,
      default: 0,
    },
  },
  mixins: [accessMixin],
  data() {
    return {
      isLoading: false,
      paginated: true,
      perPage: 10,
      isCardModalActive: false,
      numberOfReservedNumbers: 1,
      maxReservedNumbers: MAX_RESERVED_NUMBERS,
      reserving: false,
      // Rezervarile create acum, primite de la server. Cat timp lista nu e goala,
      // fereastra e in starea de confirmare.
      reservedNow: [],
      reservedRegistrationNumbers: [],
      showReserved: false,
    };
  },
  computed: {
    isConfirmation() {
      return this.reservedNow.length > 0;
    },
    confirmationTitle() {
      const count = this.reservedNow.length;
      const date = this.$options.filters.formatDate(
        this.reservedNow[0].registrationDate
      );
      const label =
        count === 1
          ? "A fost rezervat 1 numar"
          : `Au fost rezervate ${count} numere`;

      return `${label} · ${date}`;
    },
  },
  watch: {
    isCardModalActive(active) {
      if (!active) {
        this.reset();
      }
    },
  },
  methods: {
    open() {
      this.reset();
      this.isCardModalActive = true;
    },
    reset() {
      this.numberOfReservedNumbers = 1;
      this.reserving = false;
      this.reservedNow = [];
      this.showReserved = false;
    },
    adjust(delta) {
      const next = this.numberOfReservedNumbers + delta;
      if (next < 1 || next > MAX_RESERVED_NUMBERS) return;
      this.numberOfReservedNumbers = next;
    },
    toggleReserved() {
      this.showReserved = !this.showReserved;
      if (this.showReserved) this.showReservedNumbers();
    },
    showReservedNumbers() {
      this.isLoading = true;
      const { getReservedNumbers } = RegistriesService;
      getReservedNumbers(this.registryId).then((response) => {
        const data = pathOr(null, ["data"], response);
        this.reservedRegistrationNumbers = Array.isArray(data) ? data : [];
        this.isLoading = false;
      });
    },
    async reserve() {
      // Protectie la dublu click: a doua apasare nu mai trimite o cerere.
      if (this.reserving) return;
      this.reserving = true;

      const { reserveNumbers } = RegistriesService;
      const response = await reserveNumbers(
        this.registryId,
        this.numberOfReservedNumbers,
        this.$store.state.userName
      );
      this.reserving = false;

      // Serviciul inghite erorile si intoarce obiectul de eroare axios.
      const errorStatus = pathOr(null, ["response", "status"], response);
      if (errorStatus === 403) {
        notify.error(this, "Nu aveti permisiunea de a rezerva numere.");
        return;
      }
      if (errorStatus === 503) {
        notify.error(this, "Registrul este ocupat. Reincercati.");
        return;
      }

      const reserved = pathOr(null, ["data"], response);
      if (
        pathOr(null, ["status"], response) !== 200 ||
        !Array.isArray(reserved) ||
        reserved.length === 0
      ) {
        notify.error(this);
        return;
      }

      this.reservedNow = reserved;
    },
    acknowledge() {
      // Fereastra revine la alegere; numerele alocate raman in lista rezervarilor.
      this.reset();
    },
    cancel() {
      this.isCardModalActive = false;
    },
  },
};
</script>

<style scoped>
.reserve-modal {
  border-radius: 10px;
  overflow: hidden;
  width: 520px;
  max-width: 100%;
}

.reserve-modal__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.1rem 1.5rem;
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
}

.reserve-modal__title {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 1rem;
  font-weight: 700;
  color: #363636;
}

.reserve-modal__body {
  padding: 1.5rem;
  background: #fff;
  display: flex;
  flex-direction: column;
  gap: 1.25rem;
}

.reserve-modal__label {
  font-size: 0.85rem;
  font-weight: 600;
  color: #4a4a4a;
  margin-bottom: 0.65rem;
}

/* Quantity picker */
.quantity-picker {
  display: inline-flex;
  align-items: center;
  border: 1px solid #dbdbdb;
  border-radius: 8px;
  overflow: hidden;
}

.quantity-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  background: #f5f5f5;
  border: none;
  cursor: pointer;
  transition: background 0.15s;
}

.quantity-btn:hover:not(:disabled) {
  background: #e8e8e8;
}

.quantity-btn:disabled {
  opacity: 0.4;
  cursor: not-allowed;
}

.quantity-value {
  min-width: 64px;
  text-align: center;
  font-size: 1.25rem;
  font-weight: 700;
  color: #363636;
  padding: 0 0.5rem;
  border-left: 1px solid #dbdbdb;
  border-right: 1px solid #dbdbdb;
}

.reserve-modal__hint {
  font-size: 0.8rem;
  color: #7a7a7a;
}

/* Confirmation */
.confirmation__numbers {
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
}

.number-tag {
  display: inline-block;
  background: #3273dc;
  color: #fff;
  font-size: 1.5rem;
  font-weight: 700;
  padding: 0.35rem 0.85rem;
  border-radius: 6px;
}

/* Already reserved section */
.reserved-section {
  border-top: 1px solid #f0f0f0;
  padding-top: 0.75rem;
}

.reserved-toggle {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  background: none;
  border: none;
  cursor: pointer;
  font-size: 0.85rem;
  color: #4a4a4a;
  font-weight: 500;
  padding: 0;
}

.reserved-toggle:hover {
  color: #3273dc;
}

.reserved-count {
  background: #ededed;
  color: #4a4a4a;
  font-size: 0.7rem;
  font-weight: 700;
  padding: 0.1rem 0.4rem;
  border-radius: 10px;
}

.reserved-table {
  margin-top: 0.75rem;
}

/* Footer */
.reserve-modal__footer {
  display: flex;
  justify-content: flex-end;
  gap: 0.75rem;
  padding: 1rem 1.5rem;
  background: #fafafa;
  border-top: 1px solid #f0f0f0;
}

/* Transitions */
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.2s ease;
}
.fade-enter,
.fade-leave-to {
  opacity: 0;
}

::v-deep .table-mobile-sort {
  padding: 0 !important;
}
</style>
